import React from 'react';
import { Calendar, Clock, Check } from 'lucide-react';

const BasicSettingsSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  scheduleTypes 
}) => {
  // Дни недели для выбора
  const daysOfWeek = [
    { value: '1', label: 'Понедельник' },
    { value: '2', label: 'Вторник' },
    { value: '3', label: 'Среда' },
    { value: '4', label: 'Четверг' },
    { value: '5', label: 'Пятница' },
    { value: '6', label: 'Суббота' },
    { value: '0', label: 'Воскресенье' }
  ];

  // Функция для обработки изменений в выборе рабочих дней
  const handleWorkDaysChange = (e) => {
    const { value, checked } = e.target;
    
    // Обновляем массив рабочих дней
    const updatedWorkDays = checked
      ? [...formData.workDays, value].sort()
      : formData.workDays.filter(day => day !== value);
    
    // Имитируем событие для обработчика изменения в родительском компоненте
    handleInputChange({
      target: {
        name: 'workDays',
        value: updatedWorkDays
      }
    });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-medium text-gray-900 dark:text-white">Основные настройки</h3>

      {/* Тип расписания */}
      <div>
        <label htmlFor="scheduleType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Тип расписания
        </label>
        <select
          id="scheduleType"
          name="scheduleType"
          value={formData.scheduleType}
          onChange={handleInputChange}
          className={`form-select w-full ${errors.scheduleType ? 'input-error' : ''}`}
        >
          <option value="">Выберите тип расписания</option>
          {scheduleTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
        {errors.scheduleType && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.scheduleType}
          </p>
        )}
        {formData.scheduleType && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {scheduleTypes.find(t => t.id === formData.scheduleType)?.description || ''}
          </p>
        )}
      </div>

      {/* Период расписания */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Calendar className="w-4 h-4 mr-2 text-indigo-500" />
            Дата начала
          </label>
          <input
            type="date"
            id="startDate"
            name="startDate"
            value={formData.startDate}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.startDate ? 'input-error' : ''}`}
          />
          {errors.startDate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.startDate}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Calendar className="w-4 h-4 mr-2 text-indigo-500" />
            Дата окончания
          </label>
          <input
            type="date"
            id="endDate"
            name="endDate"
            value={formData.endDate}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.endDate ? 'input-error' : ''}`}
          />
          {errors.endDate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.endDate}
            </p>
          )}
        </div>
      </div>

      {/* Рабочие дни */}
      <div>
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Рабочие дни
        </span>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {daysOfWeek.map((day) => (
            <label key={day.value} className="inline-flex items-center p-2 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <input
                type="checkbox"
                name={`workDay_${day.value}`}
                value={day.value}
                checked={formData.workDays.includes(day.value)}
                onChange={handleWorkDaysChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                {day.label}
              </span>
            </label>
          ))}
        </div>
        {errors.workDays && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.workDays}
          </p>
        )}
      </div>

      {/* Учет праздничных дней */}
      <div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="holidaysInclude"
            name="holidaysInclude"
            checked={formData.holidaysInclude}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <label htmlFor="holidaysInclude" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Учитывать российские праздники
          </label>
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 ml-6">
          Автоматически исключать государственные праздники РФ из расписания
        </p>
      </div>

      {/* Примечание */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
        <div className="flex items-start">
          <Check className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              Совет по составлению расписания
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Для школьного расписания рекомендуется выбирать тип "Недельное расписание", 
              который учитывает особенности распределения учебной нагрузки по дням недели. 
              Для создания семестрового расписания ВУЗа подойдет тип "Семестровое".
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicSettingsSection;